import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { FaceLivenessDetectorCore, AwsCredentialProvider } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import { livenessService } from '../../services/livenessService';
import { CombinedResult, ValidationType } from '../../services/livenessService';

interface LivenessCheckProps {
  type: ValidationType;
  userId?: number; // necessário apenas para VERIFY
  onComplete: (result: CombinedResult) => void;
  onError: (error: string) => void;
}

const LivenessCheck: React.FC<LivenessCheckProps> = ({ type, userId, onComplete, onError }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    const initializeLiveness = async () => {
      try {
        setLoading(true);
        setError(null);

        const session = await livenessService.createSession();
        setSessionId(session.session_id);
      } catch (err) {
        const errorMessage = 'Erro ao inicializar verificação de vida. Tente novamente.';
        setError(errorMessage);
        onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeLiveness();
  }, [onError]);

  const handleAnalysisComplete = async () => {
    if (!sessionId) return;

    try {
      let result: CombinedResult;

      switch (type) {
        case 'VERIFY':
          if (!userId) throw new Error('User ID is required for verification');
          result = await livenessService.verifyWithUser(sessionId, userId);
          break;
        case 'SEARCH':
          result = await livenessService.searchUser(sessionId);
          break;
        default:
          result = {
            liveness: await livenessService.getSessionResult(sessionId)
          };
      }

      onComplete(result);
    } catch (err) {
      const errorMessage = 'Erro ao verificar resultado. Tente novamente.';
      setError(errorMessage);
      onError(errorMessage);
    }
  };

  const credentialProvider: AwsCredentialProvider = async () => {
    const token = await livenessService.getToken();
    return {
      accessKeyId: token.access_key,
      secretAccessKey: token.secret_key,
      sessionToken: token.session_token,
      expiration: new Date(token.expiration)
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => {
              const initializeLiveness = async () => {
                try {
                  setLoading(true);
                  setError(null);

                  const session = await livenessService.createSession();
                  setSessionId(session.session_id);
                } catch (err) {
                  const errorMessage = 'Erro ao inicializar verificação de vida. Tente novamente.';
                  setError(errorMessage);
                  onError(errorMessage);
                } finally {
                  setLoading(false);
                }
              };

              initializeLiveness();
            }}
          >
            {error}
          </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
      <Typography variant="h6" gutterBottom>
        Verificação de Vida
      </Typography>
      
      {sessionId && (
        <FaceLivenessDetectorCore
          sessionId={sessionId}
          region="us-east-1"
          onAnalysisComplete={handleAnalysisComplete}
          onError={(error) => {
            const errorMessage = 'Erro durante a verificação';
            setError(errorMessage);
            onError(errorMessage);
          }}
          config={{
            credentialProvider
          }}
        />
      )}
    </Box>
  );
};

export default LivenessCheck; 